import React from 'react';
import { useLocation } from 'react-router-dom';

const SEOHead = ({ 
  title, 
  description, 
  image, 
  url, 
  type = "website", 
  area = "Aylesbury",
  keywords = "",
  schema = null 
}) => {
  const location = useLocation();

  const baseUrl = "https://diamondcleaning-aylesbury.com";

  // Enhanced default values with better SEO
  const defaultTitle = `Diamond Cleaning ${area} - Professional Cleaning Services | Domestic & Commercial`;
  const defaultDescription = `Professional domestic & commercial cleaning services in ${area} and surrounding areas. Reliable, insured cleaners. End of tenancy, deep cleaning, office cleaning. Book online or call 07878 522378 for a free quote.`;
  const defaultImage = `${baseUrl}/img/logo.png`;
  const defaultKeywords = `cleaning services ${area}, domestic cleaning ${area}, commercial cleaning ${area}, end of tenancy cleaning, deep cleaning, office cleaning, communal cleaning, professional cleaners, insured cleaners, vetted cleaners, ${area} cleaners, cleaning company ${area}`;
  

  // Use provided values or defaults
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageImage = image || defaultImage;
  const pageUrl = url || `${baseUrl}${location.pathname}`;
  const pageKeywords = keywords || defaultKeywords;

  React.useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    } else {
      const newMetaDescription = document.createElement('meta');
      newMetaDescription.setAttribute('name', 'description');
      newMetaDescription.setAttribute('content', pageDescription);
      document.head.appendChild(newMetaDescription);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', pageKeywords);
    } else {
      const newMetaKeywords = document.createElement('meta');
      newMetaKeywords.setAttribute('name', 'keywords');
      newMetaKeywords.setAttribute('content', pageKeywords);
      document.head.appendChild(newMetaKeywords);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', pageTitle);
    } else {
      const newOgTitle = document.createElement('meta');
      newOgTitle.setAttribute('property', 'og:title');
      newOgTitle.setAttribute('content', pageTitle);
      document.head.appendChild(newOgTitle);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', pageDescription);
    } else {
      const newOgDescription = document.createElement('meta');
      newOgDescription.setAttribute('property', 'og:description');
      newOgDescription.setAttribute('content', pageDescription);
      document.head.appendChild(newOgDescription);
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', pageImage);
    } else {
      const newOgImage = document.createElement('meta');
      newOgImage.setAttribute('property', 'og:image');
      newOgImage.setAttribute('content', pageImage);
      document.head.appendChild(newOgImage);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', pageUrl);
    } else {
      const newOgUrl = document.createElement('meta');
      newOgUrl.setAttribute('property', 'og:url');
      newOgUrl.setAttribute('content', pageUrl);
      document.head.appendChild(newOgUrl);
    }

    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', type);
    } else {
      const newOgType = document.createElement('meta');
      newOgType.setAttribute('property', 'og:type');
      newOgType.setAttribute('content', type);
      document.head.appendChild(newOgType);
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', pageTitle);
    } else {
      const newTwitterTitle = document.createElement('meta');
      newTwitterTitle.setAttribute('property', 'twitter:title');
      newTwitterTitle.setAttribute('content', pageTitle);
      document.head.appendChild(newTwitterTitle);
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', pageDescription);
    } else {
      const newTwitterDescription = document.createElement('meta');
      newTwitterDescription.setAttribute('property', 'twitter:description');
      newTwitterDescription.setAttribute('content', pageDescription);
      document.head.appendChild(newTwitterDescription);
    }

    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', pageImage);
    } else {
      const newTwitterImage = document.createElement('meta');
      newTwitterImage.setAttribute('property', 'twitter:image');
      newTwitterImage.setAttribute('content', pageImage);
      document.head.appendChild(newTwitterImage);
    }

    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', pageUrl);
    } else {
      const newTwitterUrl = document.createElement('meta');
      newTwitterUrl.setAttribute('property', 'twitter:url');
      newTwitterUrl.setAttribute('content', pageUrl);
      document.head.appendChild(newTwitterUrl);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // Add structured data if provided
    if (schema) {
      const existingSchema = document.querySelector('script[data-schema="page-specific"]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'page-specific');
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }

    // Add hreflang for different area pages
    const existingHreflang = document.querySelectorAll('link[hreflang]');
    existingHreflang.forEach(link => link.remove());

    if (location.pathname.includes('/home/') || location.pathname.includes('/domestic/') || location.pathname.includes('/commercial/')) {
      const areas = ['aylesbury', 'milton-keynes', 'buckingham', 'princes-risborough'];
      areas.forEach(areaSlug => {
        const hreflang = document.createElement('link');
        hreflang.setAttribute('rel', 'alternate');
        hreflang.setAttribute('hreflang', 'en-GB');
        hreflang.setAttribute('href', `${baseUrl}${location.pathname.replace(/\/[^/]+$/, '')}/${areaSlug}`);
        document.head.appendChild(hreflang);
      });
    }

  }, [pageTitle, pageDescription, pageImage, pageUrl, type, pageKeywords, schema, location.pathname]);

  return null; // This component doesn't render anything visible
};

export default SEOHead;